// [PHASE B / #021]: Stamp `id` + `characterId` onto a playerStats object so
//   the in-memory state, the savedCharacters entry, AND the next autosave
//   snapshot all carry the same identifier.
//
// Pre-fix `saveCharacter` (App.jsx ~L8210) stamped these fields onto the
// savedCharacters[N].data.playerStats entry only — never onto the in-memory
// playerStats. The autosave effect then serialized the in-memory state
// (no id), refresh restored that snapshot (no id), and the party-create
// guard at SettingsPanel.jsx:2178 fired with "Save your character first."
// User reported this regression on 2026-05-06 against a Lvl 4 Barbarian
// that had been saved + autosaved + exported.
//
// Resolution priority (first non-empty wins):
//   1. fallbackId    — caller's preferred identifier (e.g. an existing
//                      savedCharacter's id, so re-saves stay stable).
//   2. playerStats.id        — already set by a prior save on this session.
//   3. playerStats.characterId — autosave-restore round-trip path.
//   4. fresh Date.now() string — first save of a brand-new character.
//
// The result has both `id` and `characterId` set to the resolved value
// — the party / Firebase code path reads `playerStats.id`, but the
// savedCharacters write path uses `characterId` for de-dupe lookup, so
// keeping them aligned avoids future drift.

/**
 * Produce a copy of `playerStats` with `id` + `characterId` stamped to a
 * single resolved identifier. Pure: never mutates the input.
 *
 * @param {object|null|undefined} playerStats
 * @param {string|null|undefined} fallbackId
 * @returns {object|null|undefined}
 */
export function stampCharacterIds(playerStats, fallbackId) {
  if (!playerStats || typeof playerStats !== 'object') return playerStats;
  const resolved =
    fallbackId ||
    playerStats.id ||
    playerStats.characterId ||
    Date.now().toString();
  return { ...playerStats, id: resolved, characterId: resolved };
}
